import React, { useState, useEffect } from 'react';
import { Divider, styled, SvgIcon } from '@teko/ui';
import { BottomSheet } from '@teko/ds-mobile';
import { removeAccent } from '../../helpers/index.es.js';
import { Input } from '../Input/Input.es.js';
import { ListItem } from '../ListItem/ListItem.es.js';
import { Text } from '../Text/Text.es.js';

const Select = ({
  value,
  items,
  type = "text",
  title,
  selectKey,
  popupTitle,
  placeholder,
  disabled,
  required,
  className,
  haveFilter,
  contentFixedHeight = true,
  onSelectChange,
  paddingBottom,
  isFilterSticky = false,
  error,
  wrapperBottomSheetHeight
}) => {
  const [keyword, setKeyword] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const onClickItem = (item) => {
    onSelectChange(item);
    setShowBottomSheet(false);
  };
  useEffect(() => {
    const normalizeKeyword = keyword && removeAccent(keyword.toLowerCase());
    if (normalizeKeyword) {
      const fItems = items.filter((item) => removeAccent(item[selectKey].toLowerCase()).includes(normalizeKeyword));
      setFilteredItems(fItems);
    } else {
      setFilteredItems(items);
    }
  }, [keyword, items]);
  const filterBlock = haveFilter && popupTitle && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FilterWrapper, null, /* @__PURE__ */ React.createElement(Input, {
    value: keyword,
    placeholder: popupTitle,
    prefixIcon: "search",
    onChange: (e) => setKeyword(e.target.value)
  })), /* @__PURE__ */ React.createElement(Divider, null)) || null;
  return /* @__PURE__ */ React.createElement(StyledSelect, {
    className
  }, title && /* @__PURE__ */ React.createElement(Label, {
    className: "select__label",
    required
  }, title), /* @__PURE__ */ React.createElement(InputContainer, null, /* @__PURE__ */ React.createElement(StyledInput, {
    type,
    value,
    disabled,
    placeholder,
    onClick: () => !disabled && setShowBottomSheet(true),
    readOnly: true,
    className: "select__input"
  }), /* @__PURE__ */ React.createElement(StyledIcon, {
    name: "chevron-forward",
    size: 14,
    className: "select__icon"
  })), !!error && /* @__PURE__ */ React.createElement(Error, null, error), /* @__PURE__ */ React.createElement(BottomSheet, {
    show: showBottomSheet,
    title: popupTitle,
    closeBottomSheet: () => setShowBottomSheet(false),
    contentHeight: contentFixedHeight ? "75vh" : void 0,
    filterNode: isFilterSticky && filterBlock,
    wrapperHeight: wrapperBottomSheetHeight
  }, /* @__PURE__ */ React.createElement(ContentWrapper, {
    paddingBottom
  }, !isFilterSticky && filterBlock, filteredItems.map((item, index) => /* @__PURE__ */ React.createElement(React.Fragment, {
    key: index
  }, /* @__PURE__ */ React.createElement(ListItem, {
    title: /* @__PURE__ */ React.createElement(Text, null, item[selectKey]),
    onClick: () => onClickItem(item)
  }), index !== items.length - 1 && /* @__PURE__ */ React.createElement(Divider, null))))));
};
const StyledSelect = styled.div((props) => {
  var _a;
  return `
    width: 100%;
    background-color: ${(_a = props.theme) == null ? void 0 : _a.colors.white};
  `;
});
const Label = styled.label(({ required, theme }) => `
    margin-bottom: 0.4rem;
    font-weight: 500;
    &::before {
      content: '*';
      display: ${required ? "inline-block" : "none"};
      margin-right: 4px;
      color: ${theme == null ? void 0 : theme.colors.error};
      font-size: 14px;
      background-color: ${theme == null ? void 0 : theme.colors.white};
    }
  `);
const InputContainer = styled.div`
  cursor: pointer;
  font-size: 13px;
  box-sizing: border-box;
  border-radius: 4px;
  position: relative;
`;
const StyledInput = styled.input((props) => {
  var _a, _b, _c, _d;
  return `
    //Remove input shadow on IOS Safari
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    outline: none;
    box-sizing: border-box;
    margin: 0;
    font-variant: tabular-nums;
    list-style: none;
    font-feature-settings: 'tnum';
    position: relative;
    display: inline-block;
    width: 100%;
    min-width: 0;
    padding: 4px 10px;
    color: ${(_a = props.theme) == null ? void 0 : _a.colors.text};
    font-size: 14px;
    line-height: 1.5715;
    background-color: ${props.disabled ? (_b = props.theme) == null ? void 0 : _b.colors.background : (_c = props.theme) == null ? void 0 : _c.colors.white};
    background-image: none;
    border: 1px solid ${(_d = props.theme) == null ? void 0 : _d.colors.border};
    border-radius: 2px;
    transition: all 0.3s;
    padding-right: 32px;
    &:hover {
      border-color: #40a9ff;
    }
  `;
});
const ContentWrapper = styled.div(({ paddingBottom }) => `
    width: 100vw;
    padding-bottom: ${paddingBottom || `0`};
  `);
const FilterWrapper = styled.div((props) => {
  var _a;
  return `
    padding: 1.75rem 1rem 0 1rem;
    input {
      border: none;
      background-color: ${(_a = props.theme) == null ? void 0 : _a.colors.background};
    }
  `;
});
const StyledIcon = styled(SvgIcon)`
  position: absolute;
  right: 0;
  cursor: pointer;
  top: 50%;
  transform: translate(-50%, -50%);
  color: rgba(0, 0, 0, 0.25);
  pointer-event: none;
  &:hover {
    color: rgba(0, 0, 0, 0.45);
  }
`;
const Error = styled.div(({ theme }) => `
    padding-top: 0;
    color: ${theme == null ? void 0 : theme.colors.error500};
    font-size: 0.75rem;
    line-height: 1rem;
    transition: color 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
    margin-top: 0.5rem;
  `);

export { Select };
